// Les phrases qui disent une offre en clair : qui propose quoi à qui, l'écart au
// prix demandé, l'échéance et la prochaine étape. Partagées par la carte de la
// liste et le haut de la fiche pour que les deux disent la même chose.
import type { JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { formatDate, formatNumber, formatPrice, type Langue } from '@artosera/shared';
import type { OffrePresentee } from '../../api/offres.js';
import { ecartAuPrixDemande, echeanceDe, type ProchaineAction } from './syntheseOffre.js';
import styles from './OffreResume.module.css';

export function PhraseOffre({ offre, langue }: { offre: OffrePresentee; langue: Langue }) {
  const premiere = offre.lignes[0];
  return (
    <p className={styles.phrase}>
      <Trans
        i18nKey="regie:offres.carte.phrase"
        count={offre.lignes.length}
        values={{
          oeuvre: premiere?.cartel.titre ?? '',
          artiste: premiere?.cartel.artiste ?? '',
          contact: offre.contactNom,
          prix: formatPrice(offre.totalCentimes, offre.devise, langue),
        }}
        components={{ b: <strong /> }}
      />
    </p>
  );
}

export function LigneEcart({ offre, langue }: { offre: OffrePresentee; langue: Langue }) {
  const { t } = useTranslation('regie');
  const ecart = ecartAuPrixDemande(offre.lignes);
  if (!ecart) return <p className={styles.ecart}>{t('offres.carte.ecart.sansPrix')}</p>;
  const valeurs = {
    montant: formatPrice(Math.abs(ecart.ecartCentimes), offre.devise, langue),
    pourcent: formatNumber(Math.abs(ecart.pourcent), langue, 1),
    demande: formatPrice(ecart.demandeCentimes, offre.devise, langue),
  };
  const cle = ecart.ecartCentimes < 0 ? 'sous' : ecart.ecartCentimes > 0 ? 'dessus' : 'egal';
  return (
    <p className={styles.ecart} data-sens={cle}>
      {t(`offres.carte.ecart.${cle}`, valeurs)}
      {ecart.partiel ? ` ${t('offres.carte.ecart.partiel')}` : ''}
    </p>
  );
}

export function LigneEcheance({
  offre,
  langue,
  maintenant,
}: {
  offre: OffrePresentee;
  langue: Langue;
  maintenant?: Date;
}): JSX.Element | null {
  const { t } = useTranslation('regie');
  if (offre.etat !== 'brouillon' && offre.etat !== 'envoyee') return null;
  const echeance = echeanceDe(offre, maintenant);
  const date = formatDate(new Date(`${offre.validiteJusquau}T12:00:00Z`), langue);
  const delai =
    echeance.jours < 0
      ? t('offres.carte.echeance.depuis', { count: -echeance.jours })
      : echeance.jours === 0
        ? t('offres.carte.echeance.aujourdhui')
        : t('offres.carte.echeance.dans', { count: echeance.jours });
  return (
    <p className={styles.echeance} data-echeance={echeance.etat}>
      {t('offres.carte.echeance.validite', { date })} · <span>{delai}</span>
    </p>
  );
}

/** « Et maintenant ? » en une phrase, selon l'état et ce qui a déjà été fait. */
export function TexteProchaineAction({
  offre,
  action,
  langue,
}: {
  offre: OffrePresentee;
  action: ProchaineAction;
  langue: Langue;
}) {
  const { t } = useTranslation('regie');
  const jour = (valeur: string | null) =>
    valeur
      ? formatDate(new Date(valeur.length <= 10 ? `${valeur}T12:00:00Z` : valeur), langue)
      : '';
  const contact = offre.contactNom;
  let texte: string;
  switch (action) {
    case 'envoyer':
      texte = t('offres.carte.action.envoyer', { contact });
      break;
    case 'relancer':
      texte = t('offres.carte.action.relancer', { contact, date: jour(offre.relancePrevueLe) });
      break;
    case 'echue':
      texte = t('offres.carte.action.echue', { contact });
      break;
    case 'attendre':
      texte = offre.relancePrevueLe
        ? t('offres.carte.action.attendreRelance', {
            contact,
            date: jour(offre.relancePrevueLe),
          })
        : t('offres.carte.action.attendre', { contact });
      break;
    case 'convertir':
      texte = t('offres.carte.action.convertir', { date: jour(offre.accepteeLe) });
      break;
    case 'reserver':
      texte = t('offres.carte.action.reserver', { date: jour(offre.accepteeLe) });
      break;
    default:
      texte = t(`offres.carte.action.rien.${offre.etat}`);
  }
  return <span className={styles.action}>{texte}</span>;
}
